/**
 * WebRTC client for connecting to a DeviceAgent's video stream.
 *
 * Uses MQTT signaling (via MQTTSignaling) to exchange SDP/ICE,
 * then renders the video track into a <video> element.
 * Control messages flow through a DataChannel.
 *
 * Automatically reconnects on connection failure with exponential backoff.
 */
import { MQTTSignaling } from "./signaling.js";
import { createViewerId } from "./id.js";
import { assertH264SendonlyVideo, assertH264VideoBundle, requireH264CodecCapabilities, } from "./camera-codec.js";
import { StreamError, StreamErrorCodes, classifyStreamError, shouldRefreshAndReconnect, shouldStopForManualRetry, } from "./stream-errors.js";
// Lightweight stats cadence. Reports are forwarded to the device-agent
// (via the control DataChannel as type="stats") so backend Prometheus
// dashboards can track real viewer-side fps / bitrate / rtt / packets
// lost. Transport liveness is handled separately by ICE state and the
// DataChannel ping watchdog; a static screen is valid and may emit no RTP.
const STATS_INTERVAL_MS = 2_000;
const MAX_RECONNECT_ATTEMPTS = 8;
const BASE_RECONNECT_DELAY_MS = 1_500;
const DISCONNECTED_GRACE_MS = 5_000;
const ICE_RESTART_TIMEOUT_MS = 5_000;
const ANSWER_TIMEOUT_MS = 10_000;
const MAX_NEGOTIATE_ATTEMPTS = 3;
// Refresh the MQTT JWT this many seconds before its `expiresAt`. The
// EMQX-issued RS256 token has a ~10 min TTL (see Runtime
// `MQTT_JWT_RSA_PRIVATE_KEY` config). Without proactive refresh, every
// reconnect after the first ~10 min hits "JWT expired" at EMQX
// CONNECT and the viewer ends up stuck on "Connecting..." until the
// page is reloaded.
const JWT_REFRESH_LEAD_SECS = 60;
// DataChannel ping 协议契约（见 scrcpy-bridge datachannel/mod.rs）：
// browser 主动发 ping，bridge 回 pong。这里 browser 每 2s 发一次
// ping，15s 内（7+ 次未回）视为控制通道挂了 —— watchdog 触发 recovery
// 即使 RTP video 侧 PeerConnection 仍报 "connected"。
const DC_PING_SEND_INTERVAL_MS = 2_000;
const DC_PING_TIMEOUT_MS = 15_000;
// How often to check the ping watchdog. Cheap; does not need to be
// aligned with stats cadence.
const DC_PING_CHECK_INTERVAL_MS = 5_000;
// sessionStorage key for the persisted viewerId. Per-tab scope (key
// is not shared across tabs) — exactly what we want: F5 in the same
// tab reuses the id so the bridge's fast path matches, but opening
// the device in a second tab gets a fresh id and the expected
// "most-recent-viewer wins" kick semantics.
const VIEWER_ID_KEY = "beeos-device-viewer-id";
/**
 * Resolve (and persist) the stable viewerId for this tab.
 *
 * The real value of a reload-stable viewerId is the KICK SEMANTICS,
 * not a zero-black-frame fast path: a reloaded page presents the same
 * viewerId, so the bridge's on_offer takes the same-viewer SILENT
 * rebuild path (scrcpy restarts, ~1-2 s black frame, no viewer_kicked)
 * instead of treating the reload as a hostile takeover. The
 * ICE-restart fast path can never apply to a reload — the browser
 * mints a fresh DTLS certificate per RTCPeerConnection and str0m pins
 * the fingerprint from the first offer, so the bridge compares
 * fingerprints and reserves the fast path for genuine
 * `createOffer({iceRestart: true})` from the same live connection.
 *
 * sessionStorage is the right tool: persistent across reload, wiped
 * on tab close, and scoped per-tab so multiple tabs don't collide.
 *
 * Falls back to an ephemeral UUID when storage is unavailable (SSR,
 * Safari private mode, quota errors) so the client still works; the
 * bridge then treats a reload as a different-viewer takeover (kick).
 */
function resolveViewerId() {
    if (typeof sessionStorage !== "undefined") {
        try {
            const existing = sessionStorage.getItem(VIEWER_ID_KEY);
            if (existing && existing.length > 0)
                return existing;
            const fresh = createViewerId();
            sessionStorage.setItem(VIEWER_ID_KEY, fresh);
            return fresh;
        }
        catch {
            // Safari private mode / storage quota — fall through to ephemeral.
        }
    }
    return createViewerId();
}
export class WebRTCClient {
    pc = null;
    signaling = null;
    dataChannel = null;
    config;
    connected = false;
    disposed = false;
    kicked = false;
    reconnectAttempt = 0;
    negotiateAttempt = 0;
    lastStreamError = null;
    reconnectTimer = null;
    disconnectedTimer = null;
    statsTimer = null;
    lastBytesReceived = 0;
    lastStatsTimestamp = 0;
    lastFramesDecoded = 0;
    streamReconnecting = false;
    iceRestartTimer = null;
    answerTimer = null;
    streamParamsRefreshTimer = null;
    streamParamsRefreshInFlight = null;
    // Recovery-flow deduplication. All signal sources (ICE state, DC
    // close/error, ping watchdog, online/visibility events) funnel into
    // `notifyUnhealthy`, which short-circuits if
    // `recovering` is already true. Cleared on the next successful
    // `connected` transition.
    recovering = false;
    // Last observed health state emitted via onHealth, to suppress
    // duplicate events.
    lastHealth = "healthy";
    // DataChannel ping watchdog.
    lastPingAt = 0;
    dcPingWatchdog = null;
    dcPingSender = null;
    // Browser-webcam uplink → device virtual camera. `cameraStream` is the
    // getUserMedia MediaStream (null when not sharing); the transceiver/sender
    // are the sendonly uplink track on the shared PeerConnection.
    cameraStream = null;
    cameraTransceiver = null;
    cameraSender = null;
    // Compositor between getUserMedia and the uplink track. The virtual
    // camera follows the standard phone-sensor contract: landscape buffer +
    // SENSOR_ORIENTATION=270, and the display chain rotates the buffer 90°
    // COUNTER-clockwise onto the portrait screen (measured on ReDroid with
    // the vHAL color-bar pattern: bars run left→right in the buffer and
    // appear top→bottom REVERSED on screen, i.e. buffer x+ maps to screen
    // up). The compositor therefore pre-rotates the webcam scene 90°
    // CLOCKWISE and sizes it so the display rotation shows it upright with
    // content height at 100% and width center-cropped. See startCamera.
    cameraCanvas = null;
    cameraCompositorVideo = null;
    cameraCompositorTimer = null;
    // Network / visibility listeners are attached once per client
    // lifetime and removed in `disconnect()` — they must survive the
    // teardown/startSession cycle that normal reconnects perform.
    networkListenersAttached = false;
    // Stable viewer identifier, pinned for the entire WebRTCClient
    // lifetime AND persisted in sessionStorage so F5 / navigation
    // within the same tab re-presents the same id to the bridge.
    // Same viewerId on reload -> silent session rebuild on the bridge
    // (no viewer_kicked); the ICE-restart fast path additionally
    // requires an unchanged DTLS fingerprint, so it only serves
    // in-place restarts from this same RTCPeerConnection. See
    // resolveViewerId() above for the storage strategy and fallback
    // behaviour.
    viewerId;
    constructor(config) {
        this.config = config;
        this.viewerId = resolveViewerId();
    }
    async connect() {
        this.disposed = false;
        this.kicked = false;
        this.reconnectAttempt = 0;
        this.attachNetworkListeners();
        // First-attempt failures (MQTT down, stream-url API hiccup, ICE
        // gather error, …) must NOT escalate to the caller and leave the
        // component in a permanent "disconnected" state — they go through
        // the same exponential-backoff recovery path as mid-session
        // failures. The caller keeps its existing `connect().catch(...)`
        // as a belt-and-braces, but in practice this `try` swallows every
        // realistic failure mode.
        try {
            // Lazily refresh if the cached params are already inside the JWT
            // refresh window (e.g. parent stashed them >9 min ago before the
            // `<DeviceViewer>` actually mounted).
            await this.maybeRefreshStreamParams("initial");
            if (this.disposed)
                return;
            await this.startSession();
            this.scheduleStreamParamsRefresh();
        }
        catch (err) {
            if (this.disposed)
                return;
            const classified = classifyStreamError(err);
            this.lastStreamError = classified;
            console.warn("[WebRTCClient] initial startSession failed:", classified.code, classified.message);
            this.teardownSession();
            if (classified.code === StreamErrorCodes.STREAM_MINT_FAILED ||
                classified.code === StreamErrorCodes.STREAM_REFRESH_FAILED) {
                this.failTerminal(classified);
                return;
            }
            this.emitHealth("reconnecting", classified.code);
            this.scheduleReconnect();
        }
    }
    disconnect() {
        this.disposed = true;
        this.connected = false;
        this.cancelReconnect();
        this.clearDisconnectedTimer();
        this.clearIceRestartTimer();
        this.clearAnswerTimer();
        this.clearStreamParamsRefreshTimer();
        this.stopDcPingWatchdog();
        this.stopDcPingSender();
        this.detachNetworkListeners();
        // Intentional teardown: tell the bridge we're going away so it
        // arms the session-hold grace window immediately instead of
        // waiting for ICE timeout. This is the ONLY call site that
        // should emit Close — reconnect-in-progress paths (scheduleReconnect,
        // reconnectForStreamRestart, attemptIceRestart fallback) must keep
        // teardownSession's default non-graceful behaviour because they
        // are coming right back under the same viewerId.
        try {
            this.signaling?.sendClose("client disconnect");
        }
        catch { /* ignore */ }
        this.teardownSession({ graceful: true });
        this.emitHealth("healthy"); // terminal — consumers unsubscribe after onDisconnected anyway
        this.config.onDisconnected?.("client disconnected");
    }
    /**
     * Returns false when the gesture cannot reach the device.
     * ICE disconnected often leaves DataChannel readyState "open" while
     * SCTP is not delivering; sending in that state looks like a successful
     * swipe and does nothing on the phone. Callers must treat false as
     * "drop this event", not as "delivered".
     */
    sendControl(message) {
        if (this.pc?.connectionState !== "connected") {
            return false;
        }
        if (this.dataChannel?.readyState !== "open") {
            return false;
        }
        this.dataChannel.send(JSON.stringify(message));
        return true;
    }
    get isConnected() {
        return this.connected;
    }
    get isCameraSharing() {
        return this.cameraStream !== null;
    }
    /**
     * Start sharing the local browser webcam into the remote device's virtual
     * camera. Captures via `getUserMedia`, adds a `sendonly` H.264 video track
     * to the existing PeerConnection, renegotiates (same viewerId + same DTLS
     * fingerprint → the bridge takes its ICE-restart/renegotiation fast path,
     * no scrcpy bounce), and signals `camera_start` on the control DataChannel
     * so the bridge configures the in-guest camera endpoint.
     *
     * H.264 is forced via `setCodecPreferences` so the uplink matches the
     * bridge's pass-through pipeline (no transcode) and the Android MediaCodec
     * hardware decoder — exactly mirroring the outbound scrcpy path's codec.
     *
     * Idempotent: a second call while already sharing is a no-op.
     */
    async startCamera(facing = "front") {
        if (this.cameraStream)
            return;
        if (!this.pc)
            throw new Error("cannot start camera before connecting");
        if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
            throw new Error("getUserMedia unavailable (needs HTTPS + a camera-permitted context)");
        }
        // Validate the transport contract before opening the physical camera. The
        // bridge deliberately does not transcode, so a non-H.264 encoder can never
        // produce a usable virtual-camera frame.
        const h264Codecs = requireH264CodecCapabilities(typeof RTCRtpSender === "undefined"
            ? undefined
            : RTCRtpSender.getCapabilities?.("video"));
        const stream = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: facing === "back" ? "environment" : "user",
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30 },
            },
            audio: false,
        });
        if (this.disposed || !this.pc) {
            stream.getTracks().forEach((t) => t.stop());
            return;
        }
        this.cameraStream = stream;
        try {
            const rawTrack = stream.getVideoTracks()[0];
            if (!rawTrack)
                throw new Error("no video track from getUserMedia");
            // Standard phone-sensor contract: the vHAL advertises a LANDSCAPE
            // 1280x720 buffer with SENSOR_ORIENTATION=270, and the display chain
            // rotates that buffer 90° COUNTER-clockwise onto the portrait screen.
            // Measured on ReDroid v12 with the vHAL color-bar pattern (bars
            // white→black along buffer x appear black at the TOP of the screen,
            // so buffer x+ maps to screen y-, which is a CCW rotation). To come
            // out upright after that rotation, composite the webcam scene rotated
            // 90° CLOCKWISE, cover-filling the frame: the portrait display then
            // shows the scene with content height at 100% and the width
            // center-cropped — no letterboxing, no distortion.
            const settings = rawTrack.getSettings();
            const fps = Math.round(settings.frameRate ?? 30);
            const frameW = 1280;
            const frameH = 720;
            const video = document.createElement("video");
            video.muted = true;
            video.playsInline = true;
            video.srcObject = stream;
            await video.play().catch(() => {
                /* playback is driven by the compositor below regardless */
            });
            const canvas = document.createElement("canvas");
            canvas.width = frameW;
            canvas.height = frameH;
            const ctx = canvas.getContext("2d");
            if (!ctx)
                throw new Error("canvas 2d context unavailable");
            const drawFrame = () => {
                const vw = video.videoWidth || settings.width || 1280;
                const vh = video.videoHeight || settings.height || 720;
                // After the display chain's 90° CCW rotation, the buffer's X axis
                // maps to the screen's -Y axis: buffer width (1280) becomes the
                // portrait screen height, buffer height (720) the screen width. We
                // draw the scene rotated +90° (CW) and cover-fill the rotated
                // footprint (vh x vw in buffer axes) so the net on-screen result is
                // an upright scene, height 100%, width center-cropped.
                const scale = Math.max(frameW / vh, frameH / vw);
                const dw = vw * scale;
                const dh = vh * scale;
                ctx.save();
                ctx.translate(frameW / 2, frameH / 2);
                ctx.rotate(Math.PI / 2);
                ctx.drawImage(video, -dw / 2, -dh / 2, dw, dh);
                ctx.restore();
            };
            ctx.fillStyle = "#000";
            ctx.fillRect(0, 0, frameW, frameH);
            drawFrame();
            this.cameraCompositorVideo = video;
            this.cameraCanvas = canvas;
            this.cameraCompositorTimer = setInterval(drawFrame, Math.max(1000 / (fps || 30), 33));
            const composited = canvas.captureStream(fps || 30);
            const track = composited.getVideoTracks()[0];
            if (!track)
                throw new Error("canvas captureStream produced no track");
            // Add a dedicated sendonly transceiver for the uplink so it stays
            // distinct from the recvonly scrcpy video m-line the bridge sends us.
            const transceiver = this.pc.addTransceiver(track, { direction: "sendonly" });
            this.cameraTransceiver = transceiver;
            this.cameraSender = transceiver.sender;
            this.requireH264(transceiver, h264Codecs);
            await this.renegotiate({ requireH264SendonlyVideo: true });
            this.sendControl({
                type: "camera_start",
                width: frameW,
                height: frameH,
                fps: fps || 30,
                facing,
            });
        }
        catch (err) {
            await this.rollbackCameraStart();
            throw err;
        }
    }
    /** Stop the rotation compositor and release its resources. */
    teardownCameraCompositor() {
        if (this.cameraCompositorTimer !== null) {
            clearInterval(this.cameraCompositorTimer);
            this.cameraCompositorTimer = null;
        }
        if (this.cameraCompositorVideo) {
            try {
                this.cameraCompositorVideo.srcObject = null;
            }
            catch { /* ignore */ }
            this.cameraCompositorVideo = null;
        }
        this.cameraCanvas = null;
    }
    /** Release every resource acquired by a camera start that did not commit. */
    async rollbackCameraStart() {
        try {
            this.cameraSender?.track?.stop();
        }
        catch { /* ignore */ }
        this.cameraStream?.getTracks().forEach((track) => track.stop());
        this.cameraStream = null;
        this.teardownCameraCompositor();
        if (this.cameraSender) {
            try {
                await this.cameraSender.replaceTrack(null);
            }
            catch { /* sender may belong to a torn-down pc */ }
        }
        if (this.cameraTransceiver) {
            try {
                this.cameraTransceiver.direction = "inactive";
            }
            catch { /* transceiver may already be stopped */ }
        }
        this.cameraSender = null;
        this.cameraTransceiver = null;
    }
    /**
     * Stop sharing the webcam: signal `camera_stop`, stop the capture tracks,
     * detach the uplink track and renegotiate so the bridge drops the inbound
     * m-line and tears down the virtual camera.
     */
    async stopCamera() {
        if (!this.cameraStream)
            return;
        this.sendControl({ type: "camera_stop" });
        // Stop the composited uplink track first, then the raw webcam capture
        // (releases the camera LED), then the compositor loop.
        try {
            this.cameraSender?.track?.stop();
        }
        catch { /* ignore */ }
        this.cameraStream.getTracks().forEach((t) => t.stop());
        this.cameraStream = null;
        this.teardownCameraCompositor();
        if (this.pc && this.cameraSender) {
            try {
                await this.cameraSender.replaceTrack(null);
            }
            catch { /* sender may already be gone on a torn-down pc */ }
        }
        if (this.cameraTransceiver) {
            try {
                this.cameraTransceiver.direction = "inactive";
            }
            catch { /* ignore */ }
        }
        this.cameraSender = null;
        if (this.pc && !this.disposed) {
            try {
                await this.renegotiate();
            }
            catch (err) {
                console.warn("[WebRTCClient] stopCamera renegotiate failed:", err);
            }
        }
        this.cameraTransceiver = null;
    }
    /**
     * Restrict a video transceiver to H.264, the bridge's only video codec.
     * This boundary is fail-closed: every bundled video m-line must share the
     * same H.264-only payload namespace before camera renegotiation can occur.
     */
    requireH264(transceiver, h264Codecs) {
        if (typeof transceiver.setCodecPreferences !== "function") {
            throw new Error("browser cannot restrict video to H.264");
        }
        try {
            // Do not append every RTX/FEC capability: many are tied to payloads for
            // VP8/VP9/AV1. Passing those orphaned entries can make browsers reject
            // the list and silently retain their default (usually VP8).
            transceiver.setCodecPreferences(h264Codecs);
        }
        catch (err) {
            throw new Error("failed to enforce H.264 video codec", { cause: err });
        }
    }
    /**
     * Create a fresh offer on the existing PeerConnection and publish it over
     * the same signaling channel. Reuses this.pc so the DTLS fingerprint is
     * unchanged and the bridge treats it as a renegotiation on the live peer
     * (no scrcpy restart). The existing `onAnswer` handler applies the answer.
     */
    async renegotiate(opts) {
        if (!this.pc || !this.signaling)
            throw new Error("no active session to renegotiate");
        const offer = await this.pc.createOffer();
        if (this.disposed || !this.pc)
            return;
        if (opts?.requireH264SendonlyVideo) {
            assertH264SendonlyVideo(offer.sdp ?? "");
        }
        await this.pc.setLocalDescription(offer);
        if (this.disposed)
            return;
        this.signaling.sendOffer(offer.sdp ?? "");
        this.armAnswerTimer();
    }
    // -- internal ---------------------------------------------------------------
    async startSession() {
        const { streamParams } = this.config;
        const iceConfig = {
            iceServers: streamParams.iceServers.map((s) => ({
                urls: s.urls,
                username: s.username,
                credential: s.credential,
            })),
            iceTransportPolicy: streamParams.iceTransportPolicy === "relay" ? "relay" : "all",
        };
        // React <StrictMode> double-invokes effects in dev, so `disconnect()`
        // may fire in-between the async steps below. Every `await` needs a
        // disposed check — otherwise a stale client goes on to publish an
        // offer MQTT and steals the scrcpy-bridge peer slot from the live
        // client (which then sees its own peer kicked 100 ms later).
        if (this.disposed)
            return;
        this.pc = new RTCPeerConnection(iceConfig);
        this.setupPeerConnectionHandlers();
        this.signaling = new MQTTSignaling(streamParams, {
            onAnswer: (sdp) => this.handleAnswer(sdp),
            onIceCandidate: (candidate) => this.handleRemoteIce(candidate),
            onError: (err) => {
                const classified = classifyStreamError(err);
                this.lastStreamError = classified;
                console.error("[WebRTCClient] Signaling error:", classified.code, classified.message);
            },
            onViewerKicked: (reason) => this.handleViewerKicked(reason),
        }, this.viewerId);
        const connectTraceId = typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `t-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        this.signaling.setTraceId(connectTraceId);
        const sessionT0 = performance.now();
        const timeline = (event, extra = "") => {
            console.info(`[WebRTCClient] event=${event} viewer=${this.viewerId.slice(0, 8)} ` +
                `trace_id=${connectTraceId} elapsed_ms=${Math.round(performance.now() - sessionT0)}` +
                (extra ? ` ${extra}` : ""));
        };
        timeline("client.connect_begin", `topic=${streamParams.deviceTopic}`);
        try {
            await this.signaling.connect();
            timeline("client.mqtt_state", "state=connected");
        }
        catch (err) {
            if (this.disposed)
                return;
            throw err;
        }
        if (this.disposed) {
            this.teardownSession();
            return;
        }
        // Both video + audio recvonly transceivers must exist BEFORE createOffer,
        // otherwise the SDP will miss the corresponding m-line and scrcpy-bridge
        // will refuse the offer ("No m-lines in offer"). scrcpy pushes audio
        // through the same PeerConnection, so we always negotiate both.
        const screenTransceiver = this.pc.addTransceiver("video", { direction: "recvonly" });
        const h264ScreenCodecs = requireH264CodecCapabilities(typeof RTCRtpReceiver === "undefined"
            ? undefined
            : RTCRtpReceiver.getCapabilities?.("video"));
        this.requireH264(screenTransceiver, h264ScreenCodecs);
        this.pc.addTransceiver("audio", { direction: "recvonly" });
        this.dataChannel = this.pc.createDataChannel("control");
        this.setupDataChannel();
        const offer = await this.pc.createOffer();
        if (this.disposed) {
            this.teardownSession();
            return;
        }
        if (!offer.sdp || !offer.sdp.includes("m=")) {
            console.error("[WebRTCClient] createOffer produced empty SDP (no m-lines), aborting");
            this.teardownSession();
            this.scheduleReconnect();
            return;
        }
        assertH264VideoBundle(offer.sdp);
        await this.pc.setLocalDescription(offer);
        if (this.disposed) {
            this.teardownSession();
            return;
        }
        this.signaling.sendOffer(offer.sdp);
        timeline("client.offer_local_set", `sdp_bytes=${offer.sdp.length}`);
        this.armAnswerTimer();
    }
    teardownSession(opts) {
        this.stopStatsCollection();
        this.stopDcPingWatchdog();
        this.stopDcPingSender();
        this.clearAnswerTimer();
        // `graceful` is only true on the `disconnect()` path so an
        // in-flight Close MQTT publish gets drained. All reconnect paths
        // use the default force-close: the browser will reoffer under
        // the same viewerId within seconds, so the bridge's grace
        // machinery (armed by ICE disconnected) does the right thing
        // without us spending extra RTTs on a Close we'd immediately
        // invalidate with a new Offer.
        this.signaling?.disconnect({ graceful: opts?.graceful === true });
        this.signaling = null;
        if (this.dataChannel) {
            try {
                this.dataChannel.close();
            }
            catch { /* ignore */ }
            this.dataChannel = null;
        }
        if (this.pc) {
            try {
                this.pc.close();
            }
            catch { /* ignore */ }
            this.pc = null;
        }
        // Release the webcam capture: the sendonly uplink track lived on the
        // now-closed PeerConnection, so it can't survive a full reconnect. The
        // UI reflects `isCameraSharing` and can re-toggle once reconnected.
        try {
            this.cameraSender?.track?.stop();
        }
        catch { /* ignore */ }
        if (this.cameraStream) {
            this.cameraStream.getTracks().forEach((t) => t.stop());
            this.cameraStream = null;
        }
        this.teardownCameraCompositor();
        this.cameraTransceiver = null;
        this.cameraSender = null;
        // Clear the MediaStream so stale tracks don't accumulate across reconnects.
        // Each new PeerConnection delivers fresh tracks via ontrack.
        this.config.videoElement.srcObject = null;
    }
    cancelReconnect() {
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    clearDisconnectedTimer() {
        if (this.disconnectedTimer !== null) {
            clearTimeout(this.disconnectedTimer);
            this.disconnectedTimer = null;
        }
    }
    clearIceRestartTimer() {
        if (this.iceRestartTimer !== null) {
            clearTimeout(this.iceRestartTimer);
            this.iceRestartTimer = null;
        }
    }
    clearAnswerTimer() {
        if (this.answerTimer !== null) {
            clearTimeout(this.answerTimer);
            this.answerTimer = null;
        }
    }
    /**
     * Stop automatic recovery and surface a stable code to the UI.
     * Callers must not leave the viewer spinning on Connecting forever.
     */
    failTerminal(error) {
        this.lastStreamError = error;
        this.cancelReconnect();
        this.clearAnswerTimer();
        this.clearIceRestartTimer();
        this.clearDisconnectedTimer();
        this.recovering = false;
        this.connected = false;
        this.teardownSession();
        this.emitHealth("reconnecting", error.code);
        this.config.onStreamError?.(error);
        this.config.onDisconnected?.(error.code);
    }
    /**
     * Manual retry from the UI after a terminal stream error.
     * Resets backoff counters and starts a fresh connect cycle that
     * always goes through refreshStreamParams when configured.
     */
    async retryConnect() {
        this.cancelReconnect();
        this.clearAnswerTimer();
        this.clearIceRestartTimer();
        this.clearDisconnectedTimer();
        this.clearStreamParamsRefreshTimer();
        this.stopDcPingWatchdog();
        this.stopDcPingSender();
        this.teardownSession();
        this.disposed = false;
        this.kicked = false;
        this.reconnectAttempt = 0;
        this.negotiateAttempt = 0;
        this.lastStreamError = null;
        this.recovering = false;
        this.connected = false;
        this.attachNetworkListeners();
        try {
            await this.maybeRefreshStreamParams("reconnect");
            if (this.disposed)
                return;
            await this.startSession();
            this.scheduleStreamParamsRefresh();
        }
        catch (err) {
            if (this.disposed)
                return;
            const classified = classifyStreamError(err);
            this.lastStreamError = classified;
            if (shouldRefreshAndReconnect(classified.code)) {
                this.emitHealth("reconnecting", classified.code);
                this.scheduleReconnect();
                return;
            }
            this.failTerminal(classified);
        }
    }
    clearStreamParamsRefreshTimer() {
        if (this.streamParamsRefreshTimer !== null) {
            clearTimeout(this.streamParamsRefreshTimer);
            this.streamParamsRefreshTimer = null;
        }
    }
    /**
     * Keep the next reconnect credential-ready without disturbing healthy
     * media. MQTT signaling is only needed for negotiation, so rotating the
     * cached params must not tear down the live PeerConnection.
     */
    scheduleStreamParamsRefresh() {
        this.clearStreamParamsRefreshTimer();
        if (this.disposed || !this.config.refreshStreamParams)
            return;
        const expiresAt = this.config.streamParams.expiresAt;
        if (!Number.isFinite(expiresAt) || expiresAt <= 0)
            return;
        const refreshAtMs = expiresAt * 1000 - JWT_REFRESH_LEAD_SECS * 1000;
        const delayMs = Math.max(0, refreshAtMs - Date.now());
        this.streamParamsRefreshTimer = setTimeout(() => {
            this.streamParamsRefreshTimer = null;
            void this.maybeRefreshStreamParams("scheduled").catch((err) => {
                if (this.disposed || this.kicked)
                    return;
                const classified = classifyStreamError(err);
                this.lastStreamError = classified;
                console.warn("[WebRTCClient] scheduled refresh escalated:", classified.code);
                // Force a full reconnect so the next session always remints via refreshStreamParams.
                this.connected = false;
                this.teardownSession();
                this.emitHealth("reconnecting", classified.code);
                this.scheduleReconnect();
            });
        }, delayMs);
    }
    armAnswerTimer() {
        this.clearAnswerTimer();
        this.answerTimer = setTimeout(() => {
            this.answerTimer = null;
            if (this.disposed || this.kicked || this.connected)
                return;
            if (this.pc?.signalingState !== "have-local-offer")
                return;
            this.negotiateAttempt += 1;
            const timeoutErr = new StreamError(StreamErrorCodes.BRIDGE_NEGOTIATE_TIMEOUT, "Answer timeout after offer — vnc-bridge negotiate timed out");
            this.lastStreamError = timeoutErr;
            console.warn(`[WebRTCClient] Answer timeout after offer (negotiate ${this.negotiateAttempt}/${MAX_NEGOTIATE_ATTEMPTS})`);
            this.connected = false;
            this.teardownSession();
            if (this.negotiateAttempt >= MAX_NEGOTIATE_ATTEMPTS) {
                this.failTerminal(timeoutErr);
                return;
            }
            this.emitHealth("reconnecting", timeoutErr.code);
            this.scheduleReconnect();
        }, ANSWER_TIMEOUT_MS);
    }
    scheduleReconnect() {
        if (this.disposed)
            return;
        if (this.reconnectTimer !== null)
            return;
        // A full reconnect supersedes any in-flight ICE restart / disconnected
        // grace timer — clear both so they don't fire after we've already
        // committed to a fresh session.
        this.clearIceRestartTimer();
        this.clearDisconnectedTimer();
        // Mark the recovery-in-flight flag so other signal sources
        // (online, visibilitychange, DC ping watchdog) don't pile on
        // redundant notifyUnhealthy calls while we wait for the timer.
        this.recovering = true;
        this.emitHealth("reconnecting");
        if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
            this.recovering = false;
            const terminal = this.lastStreamError ??
                new StreamError(StreamErrorCodes.BRIDGE_NEGOTIATE_FAILED, "max reconnect attempts reached");
            this.failTerminal(terminal);
            return;
        }
        this.reconnectAttempt++;
        const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempt - 1);
        console.log(`[WebRTCClient] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS})`);
        this.config.onReconnecting?.(this.reconnectAttempt);
        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            if (this.disposed)
                return;
            try {
                await this.maybeRefreshStreamParams("reconnect");
                if (this.disposed)
                    return;
                await this.startSession();
            }
            catch (err) {
                const classified = classifyStreamError(err);
                this.lastStreamError = classified;
                console.error("[WebRTCClient] Reconnect failed:", classified.code, classified.message);
                if (shouldStopForManualRetry(classified.code) && !shouldRefreshAndReconnect(classified.code)) {
                    // Mint / negotiate hard failures: do not spin Connecting forever.
                    if (classified.code === StreamErrorCodes.STREAM_MINT_FAILED ||
                        classified.code === StreamErrorCodes.STREAM_REFRESH_FAILED) {
                        this.failTerminal(classified);
                        return;
                    }
                }
                if (shouldRefreshAndReconnect(classified.code)) {
                    // Token path: keep limited backoff, but each attempt must refresh first.
                    this.scheduleReconnect();
                    return;
                }
                if (classified.code === StreamErrorCodes.BRIDGE_NEGOTIATE_TIMEOUT ||
                    classified.code === StreamErrorCodes.BRIDGE_NEGOTIATE_FAILED) {
                    this.negotiateAttempt += 1;
                    if (this.negotiateAttempt >= MAX_NEGOTIATE_ATTEMPTS) {
                        this.failTerminal(classified);
                        return;
                    }
                }
                this.scheduleReconnect();
            }
        }, delay);
    }
    /**
     * Refresh `this.config.streamParams` via the optional consumer hook.
     *
     * Called before a full reconnect (so EMQX never sees a stale JWT) and
     * lazily before the very first session if the cached `expiresAt`
     * already falls inside JWT_REFRESH_LEAD_MS — that covers the
     * pathological case where the consumer hands us pre-expired params
     * after a long idle gap (e.g. browser tab parked, then woken by
     * `online` / `visibilitychange`).
     *
     * Errors are intentionally non-fatal here: we log + reuse the existing
     * snapshot and let the next reconnect cycle try again. If the hook
     * needs to surface a hard auth failure (401, instance gone) it can
     * throw and `connect()` / `scheduleReconnect()` will bubble up via the
     * existing failure path, eventually hitting `MAX_RECONNECT_ATTEMPTS`
     * and reporting `"max reconnect attempts reached"`.
     */
    async maybeRefreshStreamParams(reason) {
        const refresh = this.config.refreshStreamParams;
        if (!refresh) {
            if (reason === "reconnect" || reason === "initial") {
                const exp = this.config.streamParams.expiresAt;
                const now = Math.floor(Date.now() / 1000);
                if (Number.isFinite(exp) && exp > 0 && exp <= now) {
                    throw new StreamError(StreamErrorCodes.STREAM_TOKEN_EXPIRED, "streamParams.expiresAt elapsed and refreshStreamParams is not configured");
                }
            }
            return;
        }
        if (reason === "initial") {
            const exp = this.config.streamParams.expiresAt;
            const now = Math.floor(Date.now() / 1000);
            if (exp && exp - now > JWT_REFRESH_LEAD_SECS) {
                return;
            }
        }
        if (this.streamParamsRefreshInFlight) {
            await this.streamParamsRefreshInFlight;
            return;
        }
        const refreshTask = (async () => {
            try {
                const next = await refresh();
                if (this.disposed)
                    return;
                this.config.streamParams = next;
                console.log("[WebRTCClient] streamParams refreshed", { reason, expiresAt: next.expiresAt });
                this.scheduleStreamParamsRefresh();
            }
            catch (err) {
                const classified = classifyStreamError(err);
                // Prefer mint failure when the hook is the stream-url fetch.
                const code = classified.code === StreamErrorCodes.STREAM_REFRESH_FAILED && reason !== "scheduled"
                    ? StreamErrorCodes.STREAM_MINT_FAILED
                    : classified.code;
                const wrapped = code === classified.code
                    ? classified
                    : new StreamError(code, classified.message, { cause: err });
                if (reason === "scheduled") {
                    // Preparing a future signaling session — keep healthy media.
                    // If the ticket is already past expiresAt, escalate so reconnect
                    // cannot silently reuse a dead JWT.
                    const exp = this.config.streamParams.expiresAt;
                    const now = Math.floor(Date.now() / 1000);
                    if (Number.isFinite(exp) && exp > 0 && exp <= now) {
                        throw new StreamError(StreamErrorCodes.STREAM_TOKEN_EXPIRED, wrapped.message || "scheduled refresh failed after expiresAt", { cause: err });
                    }
                    console.warn("[WebRTCClient] refreshStreamParams failed (scheduled); keeping cached params:", err);
                    return;
                }
                // initial / reconnect: never reuse a stale ticket after a failed mint.
                throw wrapped;
            }
        })();
        this.streamParamsRefreshInFlight = refreshTask;
        try {
            await refreshTask;
        }
        finally {
            if (this.streamParamsRefreshInFlight === refreshTask) {
                this.streamParamsRefreshInFlight = null;
            }
        }
    }
    setupPeerConnectionHandlers() {
        if (!this.pc)
            return;
        this.pc.ontrack = (event) => {
            console.info(`[WebRTCClient] event=client.video_track kind=${event.track.kind} ` +
                `id=${event.track.id} viewer=${this.viewerId.slice(0, 8)}`);
            const existing = this.config.videoElement.srcObject;
            if (existing) {
                existing.addTrack(event.track);
            }
            else {
                const stream = new MediaStream([event.track]);
                this.config.videoElement.srcObject = stream;
            }
            const el = this.config.videoElement;
            const onPlaying = () => {
                console.info(`[WebRTCClient] event=client.first_frame source=video_element viewer=${this.viewerId.slice(0, 8)}`);
                el.removeEventListener("playing", onPlaying);
            };
            el.addEventListener("playing", onPlaying, { once: true });
            el.play().catch(() => { });
        };
        this.pc.onicecandidate = (event) => {
            if (event.candidate && this.signaling) {
                this.signaling.sendIceCandidate(event.candidate);
            }
        };
        this.pc.onconnectionstatechange = () => {
            const state = this.pc?.connectionState;
            console.info(`[WebRTCClient] event=client.ice_state pc=${state} viewer=${this.viewerId.slice(0, 8)}`);
            if (state === "connected") {
                this.connected = true;
                this.reconnectAttempt = 0;
                this.negotiateAttempt = 0;
                this.lastStreamError = null;
                this.recovering = false;
                this.clearDisconnectedTimer();
                this.clearIceRestartTimer();
                this.clearAnswerTimer();
                this.startStatsCollection();
                this.startDcPingWatchdog();
                this.startDcPingSender();
                this.emitHealth("healthy");
                this.config.onConnected?.();
            }
            else if (state === "disconnected") {
                // Arm the 5 s grace; if the connection self-heals (str0m /
                // browser ICE re-uses the pair) we never promote to a real
                // recovery action. Otherwise we funnel into notifyUnhealthy.
                this.emitHealth("recovering", "pc disconnected");
                if (!this.disconnectedTimer) {
                    this.disconnectedTimer = setTimeout(() => {
                        this.disconnectedTimer = null;
                        if (this.pc?.connectionState === "disconnected") {
                            this.notifyUnhealthy("pc disconnected grace elapsed");
                        }
                    }, DISCONNECTED_GRACE_MS);
                }
            }
            else if (state === "failed") {
                // Funnel through `notifyUnhealthy` so the `recovering` flag
                // early-exit dedupes against an already in-flight ICE
                // restart. Without this, an ICE-restart offer that briefly
                // flips pc.connectionState to "failed" mid-handshake would
                // tear down the PeerConnection from under itself and force
                // a full reconnect. notifyUnhealthy then picks ICE restart
                // or full reconnect based on pc state (failed still has
                // signalingState=stable, so restartIce is attempted first).
                this.notifyUnhealthy("pc failed");
            }
        };
        // `iceConnectionState` transitions to `failed` / `disconnected`
        // slightly earlier than the aggregated `connectionState`, giving
        // us a tiny head start on recovery. Either signal being "failed"
        // should attempt recovery; `disconnected` we let the
        // connectionState grace timer handle so we don't duplicate work.
        this.pc.oniceconnectionstatechange = () => {
            const iceState = this.pc?.iceConnectionState;
            if (iceState === "failed") {
                this.notifyUnhealthy("ice failed");
                return;
            }
            // ICE restart 的成功信号。已连接的 PC 做 restartIce 时，
            // pc.connectionState 通常全程保持 "connected" 不 flip，
            // 而 iceConnectionState 会走一圈 checking -> connected。
            // 若此时 recovering=true 且 pc 视角仍是 connected，判定
            // 静默 ICE restart 成功，主动归位。首次建连时 recovering=false，
            // 由 onconnectionstatechange 的 connected 分支负责，这里不会误触发。
            if ((iceState === "connected" || iceState === "completed") &&
                this.recovering &&
                this.pc?.connectionState === "connected") {
                console.log("[WebRTCClient] ICE restart succeeded (ice state recovered while pc stayed connected)");
                this.connected = true;
                this.reconnectAttempt = 0;
                this.negotiateAttempt = 0;
                this.lastStreamError = null;
                this.recovering = false;
                this.clearDisconnectedTimer();
                this.clearIceRestartTimer();
                this.clearAnswerTimer();
                this.startStatsCollection();
                this.startDcPingWatchdog();
                this.startDcPingSender();
                this.emitHealth("healthy");
                this.config.onConnected?.();
            }
        };
        this.pc.ondatachannel = (event) => {
            this.dataChannel = event.channel;
            this.setupDataChannel();
        };
    }
    setupDataChannel() {
        if (!this.dataChannel)
            return;
        this.dataChannel.binaryType = "arraybuffer";
        this.dataChannel.onmessage = (event) => {
            // Audio now travels over RTP on its own m-line and is rendered
            // natively by the <video> element, so the only traffic we expect
            // here is JSON control messages from scrcpy-bridge.
            if (typeof event.data !== "string") {
                return;
            }
            try {
                const msg = JSON.parse(event.data);
                // 刷新 DC watchdog。协议契约见 scrcpy-bridge
                // datachannel/mod.rs：browser 定期发 ping，bridge 回 pong。
                // 任何合法 inbound JSON 都算链路存活证据（深度防御）——
                // 未来 bridge 若加新主动消息也自然算心跳，不用每次加类型都
                // 同步改这里。
                this.lastPingAt = Date.now();
                if (msg.type === "pong") {
                    // 心跳回包，仅用于喂 watchdog；上面已更新 lastPingAt。
                    return;
                }
                if (msg.type === "viewer_kicked") {
                    this.handleViewerKicked(msg.reason ?? "replaced by another viewer");
                    return;
                }
                if (msg.type === "stream_restarted") {
                    console.log("[WebRTCClient] stream_restarted — flush decoder, keep PeerConnection");
                    this.flushDecoderForStreamRestart();
                    return;
                }
                this.config.onDataChannelMessage?.(msg);
            }
            catch {
                // ignore
            }
        };
        // A DataChannel close without a corresponding PeerConnection
        // `failed` is surprisingly common when the server-side peer is
        // torn down cleanly but the media side hasn't noticed yet. Treat
        // it as unhealthy and let `notifyUnhealthy` decide between ICE
        // restart and full reconnect.
        this.dataChannel.onclose = () => {
            if (this.disposed || this.kicked)
                return;
            if (this.pc?.connectionState === "closed")
                return;
            console.warn("[WebRTCClient] DataChannel closed unexpectedly");
            this.notifyUnhealthy("datachannel closed");
        };
        this.dataChannel.onerror = (event) => {
            if (this.disposed || this.kicked)
                return;
            console.warn("[WebRTCClient] DataChannel error:", event);
            // Browsers fire `error` just before `close` on transport
            // failures, so this notify is effectively a no-op (dedup'd by
            // `recovering`); kept for diagnostics and to cover the rare
            // error-without-close case.
            this.notifyUnhealthy("datachannel error");
        };
    }
    /**
     * Stream Protocol v1: `stream_restarted` means the encoder process
     * was replaced. The PeerConnection stays up. Flush the media element
     * and ask for an IDR so the decoder resyncs on the next SPS/PPS.
     */
    flushDecoderForStreamRestart() {
        if (this.disposed)
            return;
        const video = this.config.videoElement;
        try {
            video.pause();
            void video.play().catch(() => { });
        }
        catch {
            // ignore
        }
        this.sendControl({ type: "request_keyframe" });
    }
    startStatsCollection() {
        this.stopStatsCollection();
        this.lastBytesReceived = 0;
        this.lastFramesDecoded = 0;
        this.lastStatsTimestamp = Date.now();
        this.statsTimer = setInterval(() => this.collectStats(), STATS_INTERVAL_MS);
    }
    stopStatsCollection() {
        if (this.statsTimer !== null) {
            clearInterval(this.statsTimer);
            this.statsTimer = null;
        }
    }
    /**
     * Sample RTCStatsReport once per cycle and forward the subset we care
     * about (bitrate / fps / rtt / packets lost) to both the consumer's
     * `onStats` callback and the backend via the control DataChannel.
     * Intentionally does NOT evaluate any "is the stream frozen?" heuristic —
     * that decision belongs on the server side, where it can correlate with
     * scrcpy encoder state. The browser only reports raw metrics.
     */
    async collectStats() {
        if (!this.pc)
            return;
        try {
            const reports = await this.pc.getStats();
            let rtt;
            reports.forEach((report) => {
                if (report.type === "candidate-pair" && report.state === "succeeded") {
                    rtt = report.currentRoundTripTime;
                }
            });
            reports.forEach((report) => {
                if (report.type === "inbound-rtp" && report.kind === "video") {
                    const now = Date.now();
                    const elapsed = (now - this.lastStatsTimestamp) / 1000;
                    const bytesReceived = report.bytesReceived ?? 0;
                    const framesDecoded = report.framesDecoded ?? 0;
                    const bitrate = elapsed > 0
                        ? ((bytesReceived - this.lastBytesReceived) * 8) / elapsed
                        : 0;
                    const decodeFps = elapsed > 0
                        ? (framesDecoded - this.lastFramesDecoded) / elapsed
                        : 0;
                    this.lastBytesReceived = bytesReceived;
                    this.lastFramesDecoded = framesDecoded;
                    this.lastStatsTimestamp = now;
                    const packetsReceived = report.packetsReceived ?? 0;
                    const stats = {
                        fps: report.framesPerSecond ?? 0,
                        packetsLost: report.packetsLost ?? 0,
                        jitter: report.jitter ?? 0,
                        bytesReceived,
                        framesDecoded,
                        roundTripTime: rtt,
                        bitrate: Math.round(bitrate),
                        timestamp: now,
                    };
                    this.sendControl({
                        type: "stats",
                        ...stats,
                        packetsReceived,
                        decodeFps: Math.round(decodeFps),
                    });
                    this.config.onStats?.(stats);
                }
            });
        }
        catch {
            // getStats can fail during teardown
        }
    }
    attemptIceRestart() {
        if (!this.pc || !this.signaling) {
            console.warn("[WebRTCClient] ICE restart skipped — no pc or signaling, falling back to full reconnect");
            this.connected = false;
            this.teardownSession();
            this.emitHealth("reconnecting", "ice restart: no signaling");
            this.scheduleReconnect();
            return;
        }
        // ICE restart only makes sense when the peer has a stable SDP — i.e. we've
        // already exchanged offer/answer at least once. Running it in any other
        // state (have-local-offer, have-remote-offer, closed) tends to emit a
        // malformed offer with no m-lines, which crashes the peer on the remote
        // end. Fall back to a full reconnect in those cases.
        if (this.pc.connectionState === "closed" || this.pc.signalingState !== "stable") {
            console.warn(`[WebRTCClient] ICE restart skipped (signalingState=${this.pc.signalingState}, connectionState=${this.pc.connectionState}) — falling back to full reconnect`);
            this.connected = false;
            this.teardownSession();
            this.emitHealth("reconnecting", "ice restart: bad state");
            this.scheduleReconnect();
            return;
        }
        // MQTT broker currently offline — publishing the restart offer would
        // be silently dropped by signaling.sendOffer, then we'd burn the 5 s
        // ICE_RESTART_TIMEOUT window waiting for an answer that can't come.
        // Short-circuit straight into scheduleReconnect so the backoff timer
        // is the only thing we wait on; startSession builds a fresh
        // MQTTSignaling which triggers a new mqtt.connect cycle.
        if (!this.signaling.isConnected()) {
            console.warn("[WebRTCClient] ICE restart skipped — MQTT offline, falling back to full reconnect");
            this.connected = false;
            this.teardownSession();
            this.emitHealth("reconnecting", "ice restart: mqtt offline");
            this.scheduleReconnect();
            return;
        }
        console.log("[WebRTCClient] Attempting ICE restart");
        this.recovering = true;
        this.emitHealth("recovering", "ice restart");
        // Prevent concurrent full-reconnect / stream-restart paths from
        // racing with the ICE restart offer.
        this.cancelReconnect();
        this.clearDisconnectedTimer();
        this.clearIceRestartTimer();
        this.iceRestartTimer = setTimeout(() => {
            this.iceRestartTimer = null;
            if (this.pc?.connectionState === "connected") {
                // ICE restart 成功兜底 —— 前面 oniceconnectionstatechange
                // 的 connected 分支是第一道防线，这里防浏览器合并/省略
                // ice state 事件（例如从头到尾未 flip 出 "connected"）。
                if (this.recovering) {
                    console.log("[WebRTCClient] ICE restart succeeded (timer fallback)");
                    this.recovering = false;
                    this.reconnectAttempt = 0;
                    this.emitHealth("healthy");
                    this.config.onConnected?.();
                }
                return;
            }
            console.warn("[WebRTCClient] ICE restart did not recover in time, falling back to full reconnect");
            this.connected = false;
            this.teardownSession();
            this.emitHealth("reconnecting", "ice restart timeout");
            this.scheduleReconnect();
        }, ICE_RESTART_TIMEOUT_MS);
        this.pc.restartIce();
        this.pc.createOffer({ iceRestart: true })
            .then((offer) => {
            if (!this.pc)
                return;
            if (!offer.sdp || !offer.sdp.includes("m=")) {
                console.warn("[WebRTCClient] ICE restart produced empty SDP, falling back to full reconnect");
                this.clearIceRestartTimer();
                this.connected = false;
                this.teardownSession();
                this.emitHealth("reconnecting", "ice restart empty sdp");
                this.scheduleReconnect();
                return;
            }
            return this.pc.setLocalDescription(offer).then(() => {
                this.signaling?.sendOffer(offer.sdp);
            });
        })
            .catch((err) => {
            console.warn("[WebRTCClient] ICE restart offer failed:", err);
            this.clearIceRestartTimer();
            this.connected = false;
            this.teardownSession();
            this.emitHealth("reconnecting", "ice restart offer error");
            this.scheduleReconnect();
        });
    }
    handleViewerKicked(reason) {
        // A client that was already disposed (e.g. React StrictMode's
        // first-mount being torn down in dev, or explicit disconnect()
        // from the host) must not bubble kick events up to the UI — the
        // live second-mount is the one the user is looking at, and a
        // stale kick from the superseded client would spuriously flip
        // App state to "Another viewer has connected". Legal kicks
        // against the live client always land here before `disposed`
        // gets set, so this guard doesn't hide real takeovers.
        if (this.disposed)
            return;
        if (this.kicked)
            return;
        this.kicked = true;
        this.disposed = true;
        this.connected = false;
        console.warn("[WebRTCClient] Viewer kicked:", reason);
        this.cancelReconnect();
        this.clearDisconnectedTimer();
        this.clearIceRestartTimer();
        this.clearAnswerTimer();
        this.stopDcPingWatchdog();
        this.stopDcPingSender();
        this.detachNetworkListeners();
        this.teardownSession();
        this.emitHealth("healthy"); // terminal
        this.config.onDisconnected?.("viewer_kicked");
    }
    async handleAnswer(sdp) {
        if (this.disposed)
            return;
        if (!this.pc || this.pc.signalingState !== "have-local-offer")
            return;
        const answer = new RTCSessionDescription({ type: "answer", sdp });
        await this.pc.setRemoteDescription(answer);
        this.clearAnswerTimer();
    }
    async handleRemoteIce(candidate) {
        if (this.disposed)
            return;
        if (!this.pc)
            return;
        try {
            await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
        }
        catch (err) {
            console.warn("[WebRTCClient] Failed to add ICE candidate:", err);
        }
    }
    // -- health / recovery plumbing --------------------------------------------
    /**
     * Single funnel for every "something looks wrong" signal source:
     * `iceConnectionState=failed`, DataChannel close/error, DC ping
     * watchdog timeout and `online` / visibility probes. Deduplicates
     * against already-in-flight recovery via the
     * `recovering` flag and picks the cheapest recovery action given
     * current pc state:
     *
     *   - no pc / kicked / disposed           → no-op
     *   - recovery already in flight          → no-op (the winner wins)
     *   - SCTP DataChannel dead reasons       → full reconnect (forced)
     *   - pc stable + signaling open          → ICE restart
     *   - otherwise                           → full reconnect
     *
     * SCTP DataChannel 死亡 (datachannel closed / error / ping timeout)
     * 必须强制 full reconnect。ICE restart 只重建 5-tuple，不会重新协商
     * SCTP — 一旦 SCTP association 死了，唯一的恢复路径就是重建
     * RTCPeerConnection + 新 offer/answer + 新 createDataChannel。
     * 历史现象：viewer 反复 ICE restart 成功 (str0m 日志显示
     * IceConnectionStateChange(Connected) + viewer connected)，但
     * 没有 ChannelOpen 事件，前端永远卡在 "Reconnecting..."。
     */
    notifyUnhealthy(reason) {
        if (this.disposed || this.kicked)
            return;
        if (this.recovering)
            return;
        if (!this.pc) {
            this.emitHealth("reconnecting", reason);
            this.scheduleReconnect();
            return;
        }
        const sctpDead = reason === "datachannel closed" ||
            reason === "datachannel error" ||
            reason === "dc ping timeout";
        if (sctpDead ||
            this.pc.connectionState === "closed" ||
            this.pc.signalingState !== "stable" ||
            !this.signaling) {
            console.log(`[WebRTCClient] notifyUnhealthy(${reason}) → full reconnect`);
            this.connected = false;
            this.teardownSession();
            this.emitHealth("reconnecting", reason);
            this.scheduleReconnect();
            return;
        }
        console.log(`[WebRTCClient] notifyUnhealthy(${reason}) → ICE restart`);
        this.attemptIceRestart();
    }
    /**
     * Emit the coarse health transition, suppressing duplicates so
     * consumers can drive UI off transitions without manual debouncing.
     */
    emitHealth(state, reason) {
        if (this.lastHealth === state)
            return;
        this.lastHealth = state;
        try {
            this.config.onHealth?.(state, reason);
        }
        catch (err) {
            console.warn("[WebRTCClient] onHealth handler threw:", err);
        }
    }
    // -- DataChannel ping watchdog ---------------------------------------------
    startDcPingWatchdog() {
        this.stopDcPingWatchdog();
        this.lastPingAt = Date.now();
        this.dcPingWatchdog = setInterval(() => {
            if (this.disposed || this.kicked)
                return;
            if (this.pc?.connectionState !== "connected")
                return;
            const silentMs = Date.now() - this.lastPingAt;
            if (silentMs > DC_PING_TIMEOUT_MS) {
                console.warn(`[WebRTCClient] DataChannel silent for ${silentMs}ms (> ${DC_PING_TIMEOUT_MS}ms) — triggering recovery`);
                // Reset the clock so we don't re-fire every tick while
                // recovery runs.
                this.lastPingAt = Date.now();
                this.notifyUnhealthy("dc ping timeout");
            }
        }, DC_PING_CHECK_INTERVAL_MS);
    }
    stopDcPingWatchdog() {
        if (this.dcPingWatchdog !== null) {
            clearInterval(this.dcPingWatchdog);
            this.dcPingWatchdog = null;
        }
    }
    startDcPingSender() {
        this.stopDcPingSender();
        this.dcPingSender = setInterval(() => {
            if (this.disposed || this.kicked)
                return;
            if (this.pc?.connectionState !== "connected")
                return;
            // sendControl returns false when PC/DC cannot deliver. Ping
            // watchdog still owns recovery; a failed send here is not a throw.
            this.sendControl({ type: "ping", ts: Date.now() });
        }, DC_PING_SEND_INTERVAL_MS);
    }
    stopDcPingSender() {
        if (this.dcPingSender !== null) {
            clearInterval(this.dcPingSender);
            this.dcPingSender = null;
        }
    }
    // -- network / visibility listeners ---------------------------------------
    attachNetworkListeners() {
        if (this.networkListenersAttached)
            return;
        if (typeof window === "undefined")
            return;
        window.addEventListener("online", this.onNetworkOnline);
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", this.onVisibilityChange);
        }
        this.networkListenersAttached = true;
    }
    detachNetworkListeners() {
        if (!this.networkListenersAttached)
            return;
        if (typeof window !== "undefined") {
            window.removeEventListener("online", this.onNetworkOnline);
        }
        if (typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", this.onVisibilityChange);
        }
        this.networkListenersAttached = false;
    }
    // Arrow-functions so `removeEventListener` sees the same identity.
    onNetworkOnline = () => {
        if (this.disposed || this.kicked)
            return;
        if (this.pc?.connectionState === "connected")
            return;
        console.log("[WebRTCClient] network online — probing recovery");
        this.notifyUnhealthy("network online");
    };
    onVisibilityChange = () => {
        if (this.disposed || this.kicked)
            return;
        if (typeof document === "undefined")
            return;
        if (document.visibilityState !== "visible")
            return;
        if (this.pc?.connectionState === "connected")
            return;
        console.log("[WebRTCClient] tab visible — probing recovery");
        this.notifyUnhealthy("tab visible");
    };
}
